/**
 * India Standard Time — the product's only timezone (a fixed +05:30, no DST).
 *
 * 🔴 "TODAY" IS AN IST DAY, WHEREVER THE BROWSER IS. A school's records are
 * dated in IST on the server (`common/date-range/ist-day-range.ts`), so a
 * "Last 7 days" built from the laptop's own clock would end on the wrong day for
 * anyone whose machine is not set to India — an operator travelling, a laptop
 * left on UTC — and between 00:00 and 05:30 IST even a UTC server would
 * disagree with the page about which day it is.
 *
 * ⚠️ SHIFT, THEN READ IN UTC. `getDate()` would read the browser's zone; adding
 * the offset to the instant and reading the UTC parts is exact because IST has
 * no DST. `Intl` is used only for formatting, never for day arithmetic.
 */

export const IST_TZ = 'Asia/Kolkata'

/** +05:30, all year. */
export const IST_OFFSET_MS = 330 * 60_000

/** The IST calendar day an instant falls in, as `YYYY-MM-DD`. */
export function istDayOf(d: Date): string {
  return new Date(d.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10)
}

/** Today in India, as `YYYY-MM-DD`. */
export function istToday(now: Date = new Date()): string {
  return istDayOf(now)
}

/**
 * Format an instant as India sees it — `Intl.DateTimeFormat` pinned to IST,
 * English (ADR-044). Pass the usual options; the zone is always IST.
 */
export function formatIst(d: Date, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  return new Intl.DateTimeFormat('en-IN', { ...opts, timeZone: IST_TZ }).format(d)
}
